import { useEffect, useRef, useState } from "react";
import type { ImportJobApiItem, ImportJobStatus } from "@recipevault/shared";
import { getImport, listImports } from "./api.js";

const TERMINAL: ImportJobStatus[] = ["done", "failed", "over_cap"];

/**
 * Polling-interval calculator. Pure function; tested in isolation.
 * 5s -> 10s after 30s no-status-change -> 30s after 60s no-status-change.
 * Returns null when the job is terminal (caller stops polling).
 */
export function computeRefetchInterval(
  data: { status: ImportJobStatus } | undefined,
  lastStatusChangeAt: number,
): number | null {
  if (!data) return 5000;
  if (TERMINAL.includes(data.status)) return null;
  const sinceChange = Date.now() - lastStatusChangeAt;
  if (sinceChange >= 60_000) return 30_000;
  if (sinceChange >= 30_000) return 10_000;
  return 5000;
}

export interface UseImportResult {
  data: ImportJobApiItem | null;
  status: "loading" | "ready" | "error";
  error: Error | null;
  refetch: () => void;
}

export function useImport(jobId: string | undefined): UseImportResult {
  const [data, setData] = useState<ImportJobApiItem | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState<Error | null>(null);
  const [lastChangeAt, setLastChangeAt] = useState(() => Date.now());
  const lastStatusRef = useRef<ImportJobStatus | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!jobId) {
      setStatus("ready");
      setData(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const fresh = await getImport(jobId);
        if (cancelled) return;
        if (lastStatusRef.current !== fresh.status) {
          lastStatusRef.current = fresh.status;
          setLastChangeAt(Date.now());
        }
        setData(fresh);
        setStatus("ready");
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId, tick]);

  // Schedule the next refetch based on backoff.
  useEffect(() => {
    if (!jobId) return;
    const interval = computeRefetchInterval(
      data ? { status: data.status } : undefined,
      lastChangeAt,
    );
    if (interval === null) return; // terminal — stop polling
    const handle = setTimeout(() => setTick((t) => t + 1), interval);
    return () => clearTimeout(handle);
  }, [jobId, data, lastChangeAt, tick]);

  return { data, status, error, refetch: () => setTick((t) => t + 1) };
}

export interface UseImportsResult {
  data: ImportJobApiItem[];
  status: "loading" | "ready" | "error";
  error: Error | null;
  refetch: () => void;
}

export function useImports(filters: { status?: ImportJobStatus } = {}): UseImportsResult {
  const [data, setData] = useState<ImportJobApiItem[]>([]);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);
  const statusFilter = filters.status;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const args: { status?: ImportJobStatus } = {};
        if (statusFilter) args.status = statusFilter;
        const res = await listImports(args);
        if (cancelled) return;
        setData(res.items);
        setStatus("ready");
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [statusFilter, tick]);

  return { data, status, error, refetch: () => setTick((t) => t + 1) };
}
